import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Slider } from "@/components/ui/slider";
import { ArrowLeft, ChevronDown, Plus, Trash2, Target, Printer, Sparkles } from "lucide-react";
import {
  LineChart, Line, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend, ReferenceDot,
} from "recharts";
import Seo from "@/components/Seo";
import ToolPrimer from "@/components/ToolPrimer";

/* ---------------- Model ---------------- */
const CONVEXITY_POWERS = { Low: 1, Normal: 2, High: 3 };

const DEFAULT_STATE = {
  clientName: "New Client",
  planningDate: new Date().toISOString().slice(0, 10),
  initialCapital: 10000,
  monthlySavings: 1000,
  annualIncomeGrowth: 2, // percent
  convexity: "Normal",
  returnShort: 3,
  returnMid: 5,
  returnLong: 7,
  extraIncomes: [],
  incomeDrops: [],
  goals: [
    { id: 1, name: "Emergency fund", amount: 15000, months: 24, priority: 1, remainsInvested: false },
    { id: 2, name: "House down payment", amount: 80000, months: 84, priority: 2, remainsInvested: false },
    { id: 3, name: "Retirement top-up", amount: 200000, months: 300, priority: 3, remainsInvested: true },
  ],
};

function simulate(state) {
  const goals = [...state.goals].sort((a, b) => a.priority - b.priority);
  if (!goals.length) return null;
  const power = CONVEXITY_POWERS[state.convexity] || 2;
  const maxMonths = Math.max(...goals.map((g) => g.months)) + 60;
  const T = maxMonths + 1;

  // Income vector (monthly savings compounded annually)
  const gYear = state.annualIncomeGrowth / 100;
  const income = new Float64Array(T);
  for (let m = 0; m < T; m++) {
    income[m] = state.monthlySavings * Math.pow(1 + gYear, m / 12);
  }
  // Apply drops — persistent from that month forward
  const dropsByMonth = {};
  state.incomeDrops.forEach((d) => {
    dropsByMonth[d.month] = (dropsByMonth[d.month] || 0) + d.pct / 100;
  });
  Object.keys(dropsByMonth).forEach((k) => {
    const idx = Number(k);
    const factor = 1 - dropsByMonth[k];
    for (let m = idx; m < T; m++) income[m] *= factor;
  });

  // Extra incomes by month
  const extrasByMonth = {};
  state.extraIncomes.forEach((e) => {
    const m = e.year * 12;
    extrasByMonth[m] = (extrasByMonth[m] || 0) + e.amount;
  });

  // Utilities by priority order (already sorted)
  let utils = goals.map((_, i) => 1 / Math.pow(i + 1, power));
  const utilSum = utils.reduce((a, b) => a + b, 0);
  utils = utils.map((u) => u / utilSum);

  // Trackers
  const goalCapital = goals.map(() => new Float64Array(T));
  const goalAlloc = goals.map(() => new Float64Array(T));
  const achieved = goals.map(() => false);
  const achievedMonth = goals.map(() => null);
  const closed = goals.map(() => false);
  const freeCash = new Float64Array(T);

  // Initial capital allocation
  let totalInit = 0;
  goals.forEach((g, j) => {
    const alloc = Math.min(state.initialCapital * utils[j], g.amount);
    goalCapital[j][0] = alloc;
    totalInit += alloc;
  });
  freeCash[0] = state.initialCapital - totalInit;

  const rateFor = (months) =>
    (months <= 36 ? state.returnShort : months <= 84 ? state.returnMid : state.returnLong) / 100;

  // Precompute the effective-monthly rate per goal so lump-sum growth over N years
  // equals (1+r_annual)^N exactly.
  const monthlyRates = goals.map((g) => Math.pow(1 + rateFor(g.months), 1 / 12) - 1);

  for (let idx = 1; idx < T; idx++) {
    freeCash[idx] = freeCash[idx - 1] + income[idx];
    if (extrasByMonth[idx]) freeCash[idx] += extrasByMonth[idx];

    // Active utils among unachieved goals
    let activeUtils = goals.map((_, j) => (achieved[j] ? 0 : utils[j]));
    const activeSum = activeUtils.reduce((a, b) => a + b, 0);
    if (activeSum > 0) activeUtils = activeUtils.map((u) => u / activeSum);

    goals.forEach((g, j) => {
      // Growth first — effective annual return, converted to equivalent monthly
      goalCapital[j][idx] = goalCapital[j][idx - 1] * (1 + monthlyRates[j]);

      if (!achieved[j] && !closed[j]) {
        const fromIncome = income[idx] * activeUtils[j];
        goalAlloc[j][idx] += fromIncome;
        const fromCash = freeCash[idx] * activeUtils[j];
        goalCapital[j][idx] += fromCash;
        freeCash[idx] -= fromCash;

        if (goalCapital[j][idx] >= g.amount && achievedMonth[j] === null) {
          achieved[j] = true;
          achievedMonth[j] = idx;
        }
      }

      if (idx > g.months && !g.remainsInvested) closed[j] = true;
      if (idx === g.months && !g.remainsInvested) {
        const spent = Math.min(g.amount, goalCapital[j][idx]);
        goalCapital[j][idx] -= spent;
        freeCash[idx] += Math.max(0, goalCapital[j][idx]);
        goalCapital[j][idx] = 0;
      }
    });
  }

  // Build chart-friendly dataset
  const dataset = [];
  for (let m = 0; m < T; m++) {
    const row = { month: m, year: +(m / 12).toFixed(2), freeCash: freeCash[m] };
    let total = freeCash[m];
    goals.forEach((g, j) => {
      row[`cap_${j}`] = goalCapital[j][m];
      row[`alloc_${j}`] = goalAlloc[j][m];
      total += goalCapital[j][m];
    });
    row.total = total;
    dataset.push(row);
  }

  // Feasibility
  const feasibility = goals.map((g, j) => {
    const check = Math.max(g.months - 1, 0);
    const available = goalCapital[j][check];
    return {
      name: g.name,
      target: g.amount,
      available,
      achievedMonth: achievedMonth[j],
      feasible: available >= g.amount,
      utilPct: utils[j] * 100,
    };
  });

  return {
    goals,
    utils,
    T,
    maxMonths,
    dataset,
    feasibility,
    finalTotal: dataset[T - 1].total,
    achievedCount: achieved.filter(Boolean).length,
    totalGoals: goals.length,
  };
}

/* ---------------- Currency & format helpers ---------------- */
const eur = (n) =>
  "\u20AC" +
  new Intl.NumberFormat("en-IE", { maximumFractionDigits: 0 }).format(Math.round(n));
const eurCompact = (n) =>
  "\u20AC" +
  new Intl.NumberFormat("en-IE", { notation: "compact", maximumFractionDigits: 1 }).format(n);
const fmtMonth = (m) => {
  const y = Math.floor(m / 12);
  const mo = m % 12;
  return y > 0 ? `${y}y ${mo}m` : `${mo}m`;
};

const PALETTE = [
  "hsl(var(--primary))",
  "hsl(var(--accent))",
  "hsl(25 40% 40%)",
  "hsl(200 40% 45%)",
  "hsl(340 40% 50%)",
  "hsl(140 30% 40%)",
  "hsl(280 30% 45%)",
  "hsl(45 55% 40%)",
];

/* ---------------- Main component ---------------- */
export default function GoalPlanner() {
  const [s, setS] = useState(DEFAULT_STATE);
  const set = (patch) => setS((p) => ({ ...p, ...patch }));

  const result = useMemo(() => simulate(s), [s]);

  const addGoal = () => {
    const id = Math.max(0, ...s.goals.map((g) => g.id)) + 1;
    set({
      goals: [
        ...s.goals,
        { id, name: `Goal ${s.goals.length + 1}`, amount: 10000, months: 36, priority: s.goals.length + 1, remainsInvested: false },
      ],
    });
  };
  const updateGoal = (id, patch) =>
    set({ goals: s.goals.map((g) => (g.id === id ? { ...g, ...patch } : g)) });
  const removeGoal = (id) => {
    const filtered = s.goals.filter((g) => g.id !== id);
    // Renormalize priorities
    filtered.sort((a, b) => a.priority - b.priority).forEach((g, i) => (g.priority = i + 1));
    set({ goals: filtered });
  };

  const addExtra = () => set({ extraIncomes: [...s.extraIncomes, { id: Date.now(), year: 5, amount: 10000 }] });
  const updateExtra = (id, patch) =>
    set({ extraIncomes: s.extraIncomes.map((e) => (e.id === id ? { ...e, ...patch } : e)) });
  const removeExtra = (id) => set({ extraIncomes: s.extraIncomes.filter((e) => e.id !== id) });

  const addDrop = () => set({ incomeDrops: [...s.incomeDrops, { id: Date.now(), month: 24, pct: 20 }] });
  const updateDrop = (id, patch) =>
    set({ incomeDrops: s.incomeDrops.map((d) => (d.id === id ? { ...d, ...patch } : d)) });
  const removeDrop = (id) => set({ incomeDrops: s.incomeDrops.filter((d) => d.id !== id) });

  const handlePrint = () => window.print();

  if (!result) return null;

  return (
    <main data-testid="planner-tool" className="max-w-[1500px] mx-auto px-6 md:px-10 py-14 md:py-20">
      <Seo
        title="Goal-Based Financial Planner"
        description="An advisor's cockpit for multi-goal financial planning. Priorities, time horizons, extraordinary incomes and life events — with feasibility check and PDF export."
        path="/tools/goal-planner"
      />
      <Link
        to="/tools"
        data-testid="planner-back"
        className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-foreground/60 hover:text-primary transition-colors mb-10 no-print"
      >
        <ArrowLeft size={14} strokeWidth={1.5} /> All tools
      </Link>

      <header className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-14 no-print">
        <div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-foreground/60 mb-3">
            Financial tool · Goal-based planner
          </div>
          <h1 className="font-serif text-5xl md:text-6xl font-light leading-[1.02] tracking-tighter">
            Every euro,
            <br />
            <span className="italic text-primary">a purpose.</span>
          </h1>
        </div>
        <p className="max-w-md text-sm text-foreground/60 leading-relaxed">
          A working advisor's cockpit. Define the goals, the constraints, the noise
          of real life — and see whether the plan actually holds together.
        </p>
      </header>

      {/* Print header (only visible when printing) */}
      <div className="print-only mb-8">
        <div className="text-xs uppercase tracking-widest text-foreground/60">Moneyssance · Financial planning report</div>
        <h1 className="font-serif text-3xl mt-2">{s.clientName}</h1>
        <div className="text-sm text-foreground/60 mt-1">Plan generated on {new Date(s.planningDate).toLocaleDateString("en-GB")}</div>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-12 gap-8 xl:gap-10">
        {/* ------- Inputs ------- */}
        <aside className="xl:col-span-4 space-y-5 no-print" data-testid="planner-inputs">
          <Panel title="Client">
            <TextField label="Client name" value={s.clientName} onChange={(v) => set({ clientName: v })} testId="pl-client" />
            <DateField label="Planning date" value={s.planningDate} onChange={(v) => set({ planningDate: v })} testId="pl-date" />
          </Panel>

          <Panel title="Baseline">
            <NumField label="Initial capital (EUR)" value={s.initialCapital} min={0} max={2000000} step={500} onChange={(v) => set({ initialCapital: v })} testId="pl-initial" />
            <NumField label="Monthly savings (EUR)" value={s.monthlySavings} min={0} max={20000} step={50} onChange={(v) => set({ monthlySavings: v })} testId="pl-monthly" />
            <SliderField label="Annual income growth" value={s.annualIncomeGrowth} min={0} max={20} step={0.1} display={`${s.annualIncomeGrowth.toFixed(1)}%`} onChange={(v) => set({ annualIncomeGrowth: v })} testId="pl-growth" last />
          </Panel>

          <Panel title="Return assumptions">
            <SliderField label="< 36 months" value={s.returnShort} min={0} max={20} step={0.1} display={`${s.returnShort.toFixed(1)}%`} onChange={(v) => set({ returnShort: v })} testId="pl-r-short" />
            <SliderField label="36 – 84 months" value={s.returnMid} min={0} max={20} step={0.1} display={`${s.returnMid.toFixed(1)}%`} onChange={(v) => set({ returnMid: v })} testId="pl-r-mid" />
            <SliderField label="> 84 months" value={s.returnLong} min={0} max={20} step={0.1} display={`${s.returnLong.toFixed(1)}%`} onChange={(v) => set({ returnLong: v })} testId="pl-r-long" last />
          </Panel>

          <Panel title="Priority allocation convexity">
            <div className="flex gap-2 mt-1">
              {["Low", "Normal", "High"].map((c) => (
                <button
                  key={c}
                  onClick={() => set({ convexity: c })}
                  data-testid={`pl-convexity-${c.toLowerCase()}`}
                  className={`flex-1 text-[11px] uppercase tracking-[0.18em] px-4 py-2.5 rounded-full border transition-colors duration-200 ${
                    s.convexity === c
                      ? "bg-primary text-primary-foreground border-primary"
                      : "border-border text-foreground/70 hover:border-primary hover:text-primary"
                  }`}
                >
                  {c}
                </button>
              ))}
            </div>
            <div className="mt-3 text-[10px] text-foreground/50 leading-relaxed italic">
              Higher convexity = more aggressive tilt toward top-priority goals.
            </div>
          </Panel>

          {/* Goals list */}
          <Panel
            title="Goals"
            side={
              <button onClick={addGoal} data-testid="pl-add-goal" className="text-xs uppercase tracking-[0.2em] text-primary hover:opacity-80">
                <Plus size={13} strokeWidth={1.5} className="inline mr-1" /> Add
              </button>
            }
          >
            {s.goals.map((g, i) => (
              <GoalCard key={g.id} g={g} i={i} onChange={(patch) => updateGoal(g.id, patch)} onRemove={() => removeGoal(g.id)} maxPriority={s.goals.length} last={i === s.goals.length - 1} />
            ))}
          </Panel>

          {/* Extraordinary incomes */}
          <Collapsible title="Extraordinary incomes" testId="pl-extras" count={s.extraIncomes.length}>
            {s.extraIncomes.map((e) => (
              <div key={e.id} className="grid grid-cols-11 gap-2 items-end mb-3">
                <div className="col-span-4">
                  <MicroLabel>Year</MicroLabel>
                  <input type="number" min={0} max={50} value={e.year} onChange={(ev) => updateExtra(e.id, { year: Math.max(0, Number(ev.target.value)) })} className={microInput} />
                </div>
                <div className="col-span-6">
                  <MicroLabel>Amount</MicroLabel>
                  <input type="number" min={0} step={500} value={e.amount} onChange={(ev) => updateExtra(e.id, { amount: Math.max(0, Number(ev.target.value)) })} className={microInput} />
                </div>
                <button onClick={() => removeExtra(e.id)} className="col-span-1 p-2 text-foreground/50 hover:text-destructive"><Trash2 size={14} strokeWidth={1.5} /></button>
              </div>
            ))}
            <button onClick={addExtra} className="w-full text-xs uppercase tracking-[0.2em] text-foreground/60 border border-dashed border-border rounded-sm py-2.5 hover:border-primary hover:text-primary transition-colors">
              <Plus size={13} strokeWidth={1.5} className="inline mr-1" /> Add income event
            </button>
          </Collapsible>

          {/* Income drops */}
          <Collapsible title="Income drops (life events)" testId="pl-drops" count={s.incomeDrops.length}>
            {s.incomeDrops.map((d) => (
              <div key={d.id} className="grid grid-cols-11 gap-2 items-end mb-3">
                <div className="col-span-4">
                  <MicroLabel>Month</MicroLabel>
                  <input type="number" min={0} max={480} value={d.month} onChange={(ev) => updateDrop(d.id, { month: Math.max(0, Number(ev.target.value)) })} className={microInput} />
                </div>
                <div className="col-span-6">
                  <MicroLabel>Drop %</MicroLabel>
                  <input type="number" min={0} max={100} value={d.pct} onChange={(ev) => updateDrop(d.id, { pct: Math.max(0, Math.min(100, Number(ev.target.value))) })} className={microInput} />
                </div>
                <button onClick={() => removeDrop(d.id)} className="col-span-1 p-2 text-foreground/50 hover:text-destructive"><Trash2 size={14} strokeWidth={1.5} /></button>
              </div>
            ))}
            <button onClick={addDrop} className="w-full text-xs uppercase tracking-[0.2em] text-foreground/60 border border-dashed border-border rounded-sm py-2.5 hover:border-primary hover:text-primary transition-colors">
              <Plus size={13} strokeWidth={1.5} className="inline mr-1" /> Add life event
            </button>
          </Collapsible>
        </aside>

        {/* ------- Outputs ------- */}
        <section className="xl:col-span-8 space-y-6" data-testid="planner-outputs">
          <SummaryHero result={result} state={s} onPrint={handlePrint} />
          <FeasibilityTable feasibility={result.feasibility} />
          <TotalWealthChart result={result} />
          <AllocationChart result={result} />
          <PerGoalGrid result={result} />
        </section>
      </div>

      <ToolPrimer
        title="Planning is allocation, not prediction."
        sections={[
          {
            heading: "What the tool does",
            body: "Each euro of monthly savings is distributed across your goals in proportion to a priority-weighted utility. Goal #1 receives more than #2, #2 more than #3, and so on — with the tilt controlled by the convexity setting (Low = mild, Normal = quadratic, High = cubic). At every month the engine grows each goal's capital at the return assumption that matches its horizon, applies life events, and checks whether the target is reached in time. The result is a plan that respects both intention and constraint.",
          },
          {
            heading: "How the returns work",
            body: "Three return regimes cover short (< 36 months), medium (36–84 months) and long (> 84 months) horizons — a rough proxy for a bond-heavy, balanced and equity-heavy portfolio. Each regime is applied as an effective annual return: r_month = (1 + r_annual)^(1/12) − 1. Lump-sum growth therefore equals (1 + r)^years exactly, which is the honest way to compound. Extraordinary incomes land in a specific year; income drops persist from that month forward, modelling parental leave, a career gap or a house purchase.",
          },
          {
            heading: "How to read the outputs",
            body: "The feasibility table is the verdict — 'Feasible' means the target is reached inside the horizon at the plan's own return assumptions. The wealth chart shows the total picture; the allocation chart shows how the monthly flow is split at each moment, and the per-goal grid isolates each trajectory so you can see whether a goal is on track, ahead or behind. Move the priorities or the convexity and the whole plan reshuffles in real time.",
          },
        ]}
        caveat="The model is deterministic — it assumes returns hit their averages every year. That never happens in practice. Use this to size the plan; use the Monte Carlo tool to stress-test whether it survives a bad decade."
      />
    </main>
  );
}

/* ---------------- UI pieces ---------------- */
function Panel({ title, side, children }) {
  return (
    <div className="border border-border rounded-sm p-5 bg-background">
      <div className="flex items-center justify-between mb-4">
        <div className="text-xs uppercase tracking-[0.25em] text-foreground/70">{title}</div>
        {side}
      </div>
      {children}
    </div>
  );
}

function Collapsible({ title, testId, count, children }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border border-border rounded-sm bg-background overflow-hidden" data-testid={testId}>
      <button onClick={() => setOpen((v) => !v)} className="w-full flex items-center justify-between px-5 py-4 text-xs uppercase tracking-[0.25em] text-foreground/70 hover:text-primary transition-colors">
        <span>
          {title} {count > 0 && <span className="ml-2 text-primary">({count})</span>}
        </span>
        <ChevronDown size={16} strokeWidth={1.5} className={`transition-transform duration-300 ${open ? "rotate-180" : ""}`} />
      </button>
      {open && <div className="px-5 pb-5 pt-1 border-t border-border">{children}</div>}
    </div>
  );
}

const microInput =
  "w-full bg-background border-b border-border focus:border-primary focus:outline-none px-1 py-1 text-sm tabular-nums";

function MicroLabel({ children }) {
  return <div className="text-[9px] uppercase tracking-[0.2em] text-foreground/50 mb-1">{children}</div>;
}

function TextField({ label, value, onChange, testId }) {
  return (
    <div className="mb-4">
      <div className="text-[11px] uppercase tracking-[0.18em] text-foreground/70 mb-2">{label}</div>
      <input value={value} onChange={(e) => onChange(e.target.value)} data-testid={testId} className="w-full bg-transparent border-b border-border focus:border-primary focus:outline-none py-1.5 text-base" />
    </div>
  );
}
function DateField({ label, value, onChange, testId }) {
  return (
    <div className="mb-1">
      <div className="text-[11px] uppercase tracking-[0.18em] text-foreground/70 mb-2">{label}</div>
      <input type="date" value={value} onChange={(e) => onChange(e.target.value)} data-testid={testId} className="w-full bg-transparent border-b border-border focus:border-primary focus:outline-none py-1.5 text-base" />
    </div>
  );
}
function NumField({ label, value, min, max, step, onChange, testId }) {
  const [raw, setRaw] = useState(String(value));
  useEffect(() => { setRaw(String(value)); }, [value]);
  const commit = () => {
    if (raw === "" || raw === "-") { setRaw(String(value)); return; }
    const n = Number(raw);
    if (!Number.isFinite(n)) { setRaw(String(value)); return; }
    const clamped = Math.max(min, Math.min(max, n));
    setRaw(String(clamped));
    if (clamped !== value) onChange(clamped);
  };
  return (
    <div className="mb-5">
      <div className="text-[11px] uppercase tracking-[0.18em] text-foreground/70 mb-2">{label}</div>
      <input type="number" value={raw} min={min} max={max} step={step}
        onChange={(e) => setRaw(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => { if (e.key === "Enter") e.currentTarget.blur(); }}
        data-testid={testId}
        className="w-full bg-transparent border-b border-border focus:border-primary focus:outline-none py-1.5 text-base font-serif tabular-nums" />
    </div>
  );
}
function SliderField({ label, value, min, max, step, display, onChange, testId, last }) {
  return (
    <div className={last ? "" : "mb-5"}>
      <div className="flex items-baseline justify-between mb-2">
        <label className="text-[11px] uppercase tracking-[0.18em] text-foreground/70">{label}</label>
        <span className="text-sm tabular-nums font-serif">{display}</span>
      </div>
      <Slider value={[value]} min={min} max={max} step={step} onValueChange={(v) => onChange(v[0])} data-testid={testId} />
    </div>
  );
}

function GoalCard({ g, i, onChange, onRemove, maxPriority, last }) {
  return (
    <div className={`border border-border rounded-sm p-4 bg-secondary/30 ${last ? "" : "mb-3"}`} data-testid={`pl-goal-${g.id}`}>
      <div className="flex items-center justify-between mb-3">
        <div className="text-[10px] uppercase tracking-[0.25em] text-primary">Goal #{i + 1}</div>
        <button onClick={onRemove} className="text-foreground/50 hover:text-destructive"><Trash2 size={14} strokeWidth={1.5} /></button>
      </div>
      <input value={g.name} onChange={(e) => onChange({ name: e.target.value })} className="w-full bg-transparent border-b border-border focus:border-primary focus:outline-none py-1 text-base font-serif mb-3" placeholder="Goal name" />
      <div className="grid grid-cols-2 gap-3">
        <div>
          <MicroLabel>Amount (EUR)</MicroLabel>
          <input type="number" min={0} step={500} value={g.amount} onChange={(e) => onChange({ amount: Math.max(0, Number(e.target.value)) })} className={microInput} />
        </div>
        <div>
          <MicroLabel>Time horizon (m)</MicroLabel>
          <input type="number" min={1} max={480} value={g.months} onChange={(e) => onChange({ months: Math.max(1, Number(e.target.value)) })} className={microInput} />
        </div>
        <div>
          <MicroLabel>Priority (1 = top)</MicroLabel>
          <input type="number" min={1} max={maxPriority} value={g.priority} onChange={(e) => onChange({ priority: Math.max(1, Math.min(maxPriority, Number(e.target.value))) })} className={microInput} />
        </div>
        <div className="flex items-end">
          <label className="inline-flex items-center gap-2 text-[10px] uppercase tracking-widest text-foreground/60 cursor-pointer">
            <input type="checkbox" checked={g.remainsInvested} onChange={(e) => onChange({ remainsInvested: e.target.checked })} />
            Stays invested
          </label>
        </div>
      </div>
    </div>
  );
}

/* ---------------- Output pieces ---------------- */
function SummaryHero({ result, state, onPrint }) {
  const rate = result.totalGoals ? result.achievedCount / result.totalGoals : 0;
  const tone = rate >= 0.9 ? "text-primary" : rate >= 0.5 ? "text-accent" : "text-destructive";
  return (
    <div className="border border-border rounded-sm p-8 md:p-10 bg-background">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
        <div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-foreground/60">Total wealth at horizon end</div>
          <div className="font-serif text-4xl md:text-5xl mt-3 tracking-tighter" data-testid="pl-final-total">{eur(result.finalTotal)}</div>
          <div className="mt-2 text-xs text-foreground/50">after {fmtMonth(result.maxMonths)}</div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-foreground/60">Goals achieved</div>
          <div className={`font-serif text-4xl md:text-5xl mt-3 tracking-tighter ${tone}`} data-testid="pl-achieved">
            {result.achievedCount} <span className="text-foreground/40">/ {result.totalGoals}</span>
          </div>
          <div className="mt-2 text-xs text-foreground/50">{(rate * 100).toFixed(0)}% of plan met</div>
        </div>
        <div className="flex flex-col justify-between items-start md:items-end">
          <div className="text-[10px] uppercase tracking-[0.3em] text-foreground/60 md:text-right">Client · {state.clientName}</div>
          <button onClick={onPrint} data-testid="pl-print" className="mt-4 inline-flex items-center gap-2 rounded-full bg-primary text-primary-foreground px-5 py-2.5 text-xs uppercase tracking-[0.2em] hover:bg-primary/90 transition-colors no-print">
            <Printer size={14} strokeWidth={1.5} /> Save as PDF
          </button>
        </div>
      </div>
    </div>
  );
}

function FeasibilityTable({ feasibility }) {
  return (
    <div className="border border-border rounded-sm bg-background overflow-hidden" data-testid="pl-feasibility">
      <div className="px-6 md:px-8 py-5 border-b border-border flex items-center gap-3">
        <Target size={16} strokeWidth={1.5} className="text-primary" />
        <div className="text-xs uppercase tracking-[0.25em] text-foreground/70">Feasibility check</div>
      </div>
      <table className="w-full text-sm">
        <thead>
          <tr className="text-[10px] uppercase tracking-widest text-foreground/50 bg-secondary/40">
            <th className="text-left px-6 md:px-8 py-3 font-normal">Goal</th>
            <th className="text-right px-4 py-3 font-normal">Target</th>
            <th className="text-right px-4 py-3 font-normal">Available at deadline</th>
            <th className="text-right px-4 py-3 font-normal">Achieved</th>
            <th className="text-right px-6 md:px-8 py-3 font-normal">Status</th>
          </tr>
        </thead>
        <tbody>
          {feasibility.map((f) => (
            <tr key={f.name} className="border-t border-border/60">
              <td className="px-6 md:px-8 py-4 font-serif">{f.name}</td>
              <td className="text-right px-4 py-4 tabular-nums">{eur(f.target)}</td>
              <td className="text-right px-4 py-4 tabular-nums">{eur(f.available)}</td>
              <td className="text-right px-4 py-4 tabular-nums text-foreground/70">{f.achievedMonth != null ? fmtMonth(f.achievedMonth) : "—"}</td>
              <td className="text-right px-6 md:px-8 py-4">
                <span className={`inline-block text-[10px] uppercase tracking-widest px-2.5 py-1 rounded-full ${f.feasible ? "bg-primary/15 text-primary" : "bg-destructive/15 text-destructive"}`}>
                  {f.feasible ? "Feasible" : "Short"}
                </span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function TotalWealthChart({ result }) {
  const data = result.dataset.filter((_, i) => i % 3 === 0 || i === result.dataset.length - 1);
  return (
    <div className="border border-border rounded-sm p-6 md:p-8 bg-background" data-testid="pl-wealth-chart">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <div className="text-xs uppercase tracking-[0.25em] text-foreground/60">Total wealth over time</div>
        <div className="text-[10px] uppercase tracking-widest text-foreground/50">
          Free cash · sum of goal capital
        </div>
      </div>
      <div className="h-[320px]">
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={data} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
            <defs>
              <linearGradient id="totalG" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="hsl(var(--primary))" stopOpacity={0.4} />
                <stop offset="100%" stopColor="hsl(var(--primary))" stopOpacity={0.05} />
              </linearGradient>
            </defs>
            <CartesianGrid stroke="hsl(var(--border))" strokeDasharray="2 4" vertical={false} />
            <XAxis dataKey="month" tickFormatter={(m) => `${Math.round(m / 12)}y`} tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 11 }} tickLine={false} axisLine={{ stroke: "hsl(var(--border))" }} />
            <YAxis tickFormatter={eurCompact} tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 11 }} tickLine={false} axisLine={false} width={65} />
            <Tooltip content={<WealthTip />} />
            <Area type="monotone" dataKey="total" stroke="hsl(var(--primary))" strokeWidth={2.5} fill="url(#totalG)" isAnimationActive={false} />
            <Line type="monotone" dataKey="freeCash" stroke="hsl(var(--muted-foreground))" strokeWidth={1.25} strokeDasharray="3 3" dot={false} isAnimationActive={false} />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

function AllocationChart({ result }) {
  const data = result.dataset.filter((_, i) => i % 3 === 0 || i === result.dataset.length - 1);
  return (
    <div className="border border-border rounded-sm p-6 md:p-8 bg-background" data-testid="pl-alloc-chart">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <div className="text-xs uppercase tracking-[0.25em] text-foreground/60">Monthly savings allocation</div>
        <div className="flex flex-wrap gap-3 text-[10px] uppercase tracking-widest text-foreground/60">
          {result.goals.map((g, j) => (
            <span key={g.id} className="inline-flex items-center gap-1.5">
              <span className="w-3 h-3 rounded-sm" style={{ background: PALETTE[j % PALETTE.length] }} />
              {g.name}
            </span>
          ))}
        </div>
      </div>
      <div className="h-[280px]">
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={data} margin={{ top: 8, right: 16, left: 0, bottom: 0 }} stackOffset="none">
            <CartesianGrid stroke="hsl(var(--border))" strokeDasharray="2 4" vertical={false} />
            <XAxis dataKey="month" tickFormatter={(m) => `${Math.round(m / 12)}y`} tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 11 }} tickLine={false} axisLine={{ stroke: "hsl(var(--border))" }} />
            <YAxis tickFormatter={eurCompact} tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 11 }} tickLine={false} axisLine={false} width={65} />
            <Tooltip content={<AllocTip />} />
            {result.goals.map((g, j) => (
              <Area
                key={g.id}
                type="monotone"
                stackId="alloc"
                dataKey={`alloc_${j}`}
                name={g.name}
                stroke={PALETTE[j % PALETTE.length]}
                fill={PALETTE[j % PALETTE.length]}
                fillOpacity={0.5}
                strokeWidth={1}
                isAnimationActive={false}
              />
            ))}
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

function PerGoalGrid({ result }) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-testid="pl-goal-grid">
      {result.goals.map((g, j) => {
        const data = result.dataset.filter((_, i) => i % 3 === 0);
        return (
          <div key={g.id} className="border border-border rounded-sm p-6 bg-background" data-testid={`pl-goal-chart-${g.id}`}>
            <div className="flex items-start justify-between mb-3 gap-3">
              <div>
                <div className="font-serif text-xl leading-tight">{g.name}</div>
                <div className="text-[10px] uppercase tracking-widest text-foreground/50 mt-1">
                  {eur(g.amount)} · {fmtMonth(g.months)} · priority {g.priority}
                </div>
              </div>
              <div
                className="text-[10px] uppercase tracking-widest px-2.5 py-1 rounded-full"
                style={{
                  background: result.feasibility[j].feasible ? "hsl(var(--primary) / 0.15)" : "hsl(var(--destructive) / 0.15)",
                  color: result.feasibility[j].feasible ? "hsl(var(--primary))" : "hsl(var(--destructive))",
                }}
              >
                {result.feasibility[j].feasible ? "Feasible" : "Short"}
              </div>
            </div>
            <div className="h-[180px] mt-3">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={data} margin={{ top: 4, right: 4, left: -10, bottom: 0 }}>
                  <CartesianGrid stroke="hsl(var(--border))" strokeDasharray="2 4" vertical={false} />
                  <XAxis dataKey="month" tickFormatter={(m) => `${Math.round(m / 12)}y`} tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 10 }} tickLine={false} axisLine={{ stroke: "hsl(var(--border))" }} />
                  <YAxis tickFormatter={eurCompact} tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 10 }} tickLine={false} axisLine={false} width={55} />
                  <Tooltip content={<GoalTip goalName={g.name} target={g.amount} />} />
                  <Line type="monotone" dataKey={`cap_${j}`} stroke={PALETTE[j % PALETTE.length]} strokeWidth={2} dot={false} isAnimationActive={false} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
        );
      })}
    </div>
  );
}

/* ---------------- Tooltips ---------------- */
function WealthTip({ active, payload, label }) {
  if (!active || !payload || !payload.length) return null;
  const total = payload.find((p) => p.dataKey === "total")?.value ?? 0;
  const fc = payload.find((p) => p.dataKey === "freeCash")?.value ?? 0;
  return (
    <div className="border border-border bg-background/95 backdrop-blur-md rounded-sm px-4 py-3 shadow-sm">
      <div className="text-[10px] uppercase tracking-widest text-foreground/50 mb-1">Month {label} · {fmtMonth(label)}</div>
      <div className="font-serif text-lg text-primary">{eur(total)}</div>
      <div className="text-xs text-foreground/60 mt-1">Free cash: {eur(fc)}</div>
    </div>
  );
}
function AllocTip({ active, payload, label }) {
  if (!active || !payload || !payload.length) return null;
  return (
    <div className="border border-border bg-background/95 backdrop-blur-md rounded-sm px-4 py-3 shadow-sm">
      <div className="text-[10px] uppercase tracking-widest text-foreground/50 mb-2">Month {label} · {fmtMonth(label)}</div>
      <div className="text-xs space-y-0.5">
        {payload.map((p) => (
          <div key={p.dataKey} className="flex items-center gap-2" style={{ color: p.color }}>
            <span className="w-2 h-2 rounded-sm shrink-0" style={{ background: p.color, opacity: 0.5 }} />
            <span>{p.name}: {eur(p.value)}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
function GoalTip({ active, payload, label, goalName, target }) {
  if (!active || !payload || !payload.length) return null;
  const val = payload[0].value;
  const pct = target ? Math.min(100, (val / target) * 100) : 0;
  return (
    <div className="border border-border bg-background/95 backdrop-blur-md rounded-sm px-4 py-3 shadow-sm">
      <div className="text-[10px] uppercase tracking-widest text-foreground/50 mb-1">{fmtMonth(label)}</div>
      <div className="font-serif text-base">{eur(val)}</div>
      <div className="text-xs text-foreground/60 mt-1">{pct.toFixed(0)}% of target</div>
    </div>
  );
}
